import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, Search, UserPlus, UserCog, Briefcase, Bus, Building, 
  Shield, Heart, Eye, Upload, FileText, Download, X, Trash2, Edit, Phone, Mail, MapPin, Calendar, CreditCard, UserX, UserCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  administrative: Briefcase,
  support: Users,
  technical: UserCog,
  transport: Bus,
  hostel: Building,
  medical: Heart,
  security: Shield,
};

const StaffPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [categories, setCategories] = useState({});
  const [stats, setStats] = useState({ total: 0, by_category: {} });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [pagination, setPagination] = useState(null);
  
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [documentTypes, setDocumentTypes] = useState({});
  const [staffDocuments, setStaffDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusStaff, setStatusStaff] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    fetchData(1);
  }, [activeTab]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      if (activeTab !== 'all') {
        params.append('category', activeTab);
      }
      
      const [catRes, statsRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff/categories`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff?${params.toString()}`, { headers: getAuthHeader() })
      ]);
      setCategories(catRes.data);
      setStats(statsRes.data);
      
      // Handle paginated response
      if (Array.isArray(staffRes.data)) {
        setStaffList(staffRes.data);
        setPagination(null);
      } else {
        setStaffList(staffRes.data.items || []);
        setPagination({
          page: staffRes.data.page,
          total: staffRes.data.total,
          total_pages: staffRes.data.total_pages,
          has_next: staffRes.data.has_next,
          has_prev: staffRes.data.has_prev
        });
      }
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch staff data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff-types`, { headers: getAuthHeader() });
      setDocumentTypes(res.data);
    } catch (error) {
      console.error('Failed to fetch document types');
    }
  };

  const fetchStaffDocuments = async (staffId) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/staff/${staffId}`, { headers: getAuthHeader() });
      setStaffDocuments(res.data.documents || []);
    } catch (error) {
      setStaffDocuments([]);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file || !selectedStaff) return;

    // File size validation - Photos: 100 KB, Documents: 300 KB
    const isPhoto = docType === 'staff_photo';
    const maxSize = isPhoto ? 100 * 1024 : 300 * 1024;
    const maxSizeLabel = isPhoto ? '100 KB' : '300 KB';
    
    if (file.size > maxSize) {
      toast.error(`File size too large! Maximum allowed: ${maxSizeLabel}. Your file: ${(file.size / 1024).toFixed(1)} KB`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);

    try {
      await axios.post(
        `${API_URL}/api/documents/staff/upload/staff/${selectedStaff.id}`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded!');
      fetchStaffDocuments(selectedStaff.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/download/${docId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/staff/${docId}`, { headers: getAuthHeader() });
      toast.success('Document deleted');
      fetchStaffDocuments(selectedStaff.id);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Open status change dialog
  const openStatusDialog = (staff, action) => {
    setStatusStaff(staff);
    if (action === 'deactivate') {
      setNewStatus('resigned');
    } else if (action === 'activate') {
      setNewStatus('active');
    }
    setStatusReason('');
    setStatusDialogOpen(true);
  };

  // Update staff status
  const updateStaffStatus = async () => {
    if (!statusStaff || !newStatus) return;
    
    setStatusLoading(true);
    try {
      if (newStatus === 'active') {
        // Reactivate - need to update directly since there's no reactivate endpoint
        await axios.put(
          `${API_URL}/api/staff/${statusStaff.id}`,
          { status: 'active' },
          { headers: getAuthHeader() }
        );
      } else {
        // Deactivate using existing endpoint
        await axios.delete(`${API_URL}/api/staff/${statusStaff.id}`, { headers: getAuthHeader() });
      }
      toast.success(`Staff ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setStatusDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Download ID Card
  const downloadIdCard = async (staffId) => {
    try {
      const res = await axios.get(`${API_URL}/api/id-cards/staff/${staffId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `id_card_staff_${staffId?.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('ID Card downloaded!');
    } catch (error) {
      toast.error('Failed to download ID Card');
    }
  };

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  const handleDelete = async (staffId) => {
    if (!confirm('Are you sure you want to deactivate this staff member?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/staff/${staffId}`, { headers: getAuthHeader() });
      toast.success('Staff member deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate staff');
    }
  };

  const [staffPhotoUrl, setStaffPhotoUrl] = useState(null);

  const openViewDialog = (staff) => {
    setSelectedStaff(staff);
    setStaffPhotoUrl(null);
    setViewDialog(true);
    fetchStaffDocuments(staff.id);
    // Fetch photo
    fetchStaffPhoto(staff.id);
  };

  const fetchStaffPhoto = async (staffId) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/staff/${staffId}`, { headers: getAuthHeader() });
      const docs = res.data.documents || [];
      // Get latest photo (sort by uploaded_at descending)
      const photos = docs.filter(d => d.document_type === 'staff_photo')
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      const photoDoc = photos[0];
      if (photoDoc?.cloudinary_url) {
        setStaffPhotoUrl(photoDoc.cloudinary_url);
      } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
        setStaffPhotoUrl(photoDoc.file_path);
      } else {
        setStaffPhotoUrl(null);
      }
    } catch (error) {
      setStaffPhotoUrl(null);
    }
  };

  const filteredStaff = staffList.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.employee_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.designation?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categoryTabs = [
    { id: 'all', label: 'All Staff', icon: Users },
    ...Object.entries(categories).map(([key, val]) => ({
      id: key,
      label: val.label,
      icon: CATEGORY_ICONS[key] || Users
    }))
  ];

  return (
    <DashboardLayout title="Non-Teaching Staff">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-primary/10">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Staff</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_category || {}).map(([cat, count]) => {
            const Icon = CATEGORY_ICONS[cat] || Users;
            return (
              <Card key={cat}>
                <CardContent className="p-3 text-center">
                  <Icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground capitalize">{cat}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, ID, designation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={() => navigate('/staff/new')} data-testid="add-staff-btn">
            <UserPlus className="h-4 w-4 mr-2" /> New Staff
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap h-auto">
            {categoryTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="text-xs">
                  <Icon className="h-3 w-3 mr-1" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredStaff.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No staff members found</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredStaff.map((staff, index) => {
                  const Icon = CATEGORY_ICONS[staff.category] || Users;
                  const isInactive = staff.status === 'inactive' || staff.status === 'resigned';
                  return (
                    <motion.div
                      key={staff.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <Card className={`hover:shadow-md transition-shadow ${isInactive ? 'opacity-60 bg-muted/30' : ''}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <CardTitle className="text-sm">{staff.name}</CardTitle>
                                <CardDescription className="text-xs">{staff.employee_id}</CardDescription>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                              <Badge variant="secondary" className="text-xs capitalize">{staff.category}</Badge>
                              <Badge variant={isInactive ? 'destructive' : 'default'} className="text-xs">
                                {staff.status || 'active'}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="text-sm">
                            <p className="font-medium">{staff.designation}</p>
                            <p className="text-muted-foreground">{staff.phone}</p>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => openViewDialog(staff)}>
                              <Eye className="h-3 w-3 mr-1" /> View
                            </Button>
                            {(!staff.status || staff.status === 'active') ? (
                              <Button size="sm" variant="ghost" className="text-orange-500" onClick={() => openStatusDialog(staff, 'deactivate')}>
                                <UserX className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="text-green-500" onClick={() => openStatusDialog(staff, 'activate')}>
                                <UserCheck className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
            
            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {pagination.total_pages} ({pagination.total} total staff)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchData(currentPage - 1)}
                    disabled={!pagination.has_prev}
                    data-testid="staff-prev-page"
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchData(currentPage + 1)}
                    disabled={!pagination.has_next}
                    data-testid="staff-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newStatus === 'active' ? 'Reactivate Staff' : 'Deactivate Staff'}
            </DialogTitle>
            <DialogDescription>
              {newStatus === 'active' 
                ? `Are you sure you want to reactivate ${statusStaff?.name}?`
                : `Are you sure you want to deactivate ${statusStaff?.name}? Their login will be blocked and salary generation will stop.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {newStatus !== 'active' && (
              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea 
                  placeholder="Enter reason for deactivation..."
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={updateStaffStatus} 
              disabled={statusLoading}
              variant={newStatus === 'active' ? 'default' : 'destructive'}
            >
              {statusLoading ? 'Updating...' : (newStatus === 'active' ? 'Reactivate' : 'Deactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Staff Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Staff Details & Documents</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="documents">Documents ({staffDocuments.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {staffPhotoUrl ? (
                      <img src={staffPhotoUrl} alt={selectedStaff.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-bold text-primary">{selectedStaff.name?.charAt(0)}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{selectedStaff.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedStaff.employee_id}</p>
                    <Badge className="mt-1 capitalize">{selectedStaff.category}</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Designation</Label>
                    <p className="font-medium">{selectedStaff.designation}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <p className="font-medium">{selectedStaff.phone}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Joining Date</Label>
                    <p className="font-medium">{selectedStaff.joining_date}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Employment Type</Label>
                    <p className="font-medium capitalize">{selectedStaff.employment_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Salary</Label>
                    <p className="font-medium">₹{selectedStaff.basic_salary?.toLocaleString() || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Shift</Label>
                    <p className="font-medium capitalize">{selectedStaff.shift_timing || 'General'}</p>
                  </div>
                </div>
                
                {selectedStaff.present_address && (
                  <div>
                    <Label className="text-muted-foreground">Address</Label>
                    <p className="text-sm">{selectedStaff.present_address}, {selectedStaff.city}, {selectedStaff.district}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="documents" className="space-y-4">
                {/* Uploaded Documents */}
                {staffDocuments.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Uploaded Documents</h4>
                    <div className="grid gap-2">
                      {staffDocuments.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-2 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{doc.document_label}</p>
                              <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadDocument(doc.id, doc.original_filename)}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteDocument(doc.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload New Documents */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Upload Documents</h4>
                  <div className="grid md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {Object.entries(documentTypes).map(([key, info]) => {
                      const isUploaded = staffDocuments.some(d => d.document_type === key);
                      return (
                        <div key={key} className={`p-2 border rounded-lg ${isUploaded ? 'bg-green-50 border-green-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-medium">{info.label}</p>
                              {info.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                            </div>
                            {!isUploaded && (
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                  onChange={(e) => handleDocumentUpload(e, key)}
                                  disabled={uploading}
                                />
                                <div className="p-1 hover:bg-muted rounded">
                                  <Upload className="h-4 w-4 text-primary" />
                                </div>
                              </label>
                            )}
                            {isUploaded && <Badge variant="default" className="text-xs">✓</Badge>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { setViewDialog(false); navigate(`/staff/edit/${selectedStaff?.id}`); }}>
              <Edit className="h-4 w-4 mr-2" /> Edit Staff
            </Button>
            <Button variant="outline" onClick={() => downloadIdCard(selectedStaff?.id)}>
              <CreditCard className="h-4 w-4 mr-2" /> ID Card
            </Button>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default StaffPage;
